import { signal } from "@preact/signals";
import type { ClientOp, ServerMessage, TournamentSnapshot } from "../../shared/protocol.js";
import {
  getAuth,
  getCache,
  getOutbox,
  setCache,
  setOutbox,
  type QueuedOp,
  type Role,
} from "./storage.js";
import { syncOrganizerSeat } from "./identity.js";

export type ConnStatus = "connecting" | "online" | "offline";

/** WebSocket sync client with an offline outbox (spec §7). Local writes apply to
 * `snapshot` immediately and never wait on the network; the outbox flushes whatever
 * didn't make it out as soon as the socket reconnects. */
export class TournamentClient {
  readonly code: string;
  /** Public so `getClient` can tell whether a cached connection is still the one this
   * device's stored auth calls for — see the note there. */
  readonly token: string;
  readonly role: Role;
  private ws: WebSocket | null = null;
  private reconnectDelay = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private closed = false;

  snapshot = signal<TournamentSnapshot | null>(null);
  /**
   * True once a `state` frame has arrived from the server. `snapshot` is seeded
   * synchronously from localStorage in the constructor so screens paint instantly, which
   * means "snapshot is non-null" says nothing about whether it is current. Anything that
   * *writes back* what it read — the setup editor sends the whole roster — has to wait
   * for this, or it will save a stale copy over newer server state.
   */
  hydrated = signal(false);
  status = signal<ConnStatus>("connecting");
  outboxSize = signal(0);
  /** Set when the server rejects a queued change; cleared after a few seconds or
   * on the next successful op. The server follows a rejection with a fresh "state"
   * message, so the optimistic UI is already reverted by the time this shows. */
  lastError = signal<string | null>(null);
  private lastErrorTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(code: string, token: string, role: Role) {
    this.code = code;
    this.token = token;
    this.role = role;
    this.snapshot.value = getCache(code);
    this.outboxSize.value = getOutbox(code).length;
    this.connect();
  }

  private connect() {
    if (this.closed) return;
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${location.host}/api/tournament/${encodeURIComponent(this.code)}/ws`;
    this.status.value = "connecting";

    // The token rides as the WS subprotocol rather than a ?token= query param, so it
    // doesn't end up in server/proxy access logs or browser devtools network history.
    // The browser validates that value against the WS subprotocol grammar and throws
    // *synchronously* for anything else — a corrupted stored token (or a call site
    // that skipped its own validation) must not be able to crash the whole app here.
    let ws: WebSocket;
    try {
      ws = new WebSocket(url, this.token);
    } catch {
      // A malformed token will never become valid on retry, so don't schedule one —
      // just surface "offline" instead of crashing.
      this.status.value = "offline";
      return;
    }
    this.ws = ws;

    ws.addEventListener("open", () => {
      this.status.value = "online";
      this.reconnectDelay = 1000;
      this.flushOutbox();
    });
    ws.addEventListener("close", () => {
      if (this.closed) return;
      this.status.value = "offline";
      this.scheduleReconnect();
    });
    ws.addEventListener("error", () => ws.close());
    ws.addEventListener("message", (event) => {
      try {
        this.handleMessage(JSON.parse(event.data as string));
      } catch {
        // ignore malformed frames
      }
    });
  }

  private scheduleReconnect() {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => this.connect(), this.reconnectDelay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
  }

  private handleMessage(msg: ServerMessage) {
    if (msg.type === "state") {
      const snapshot: TournamentSnapshot = {
        tournament: msg.tournament,
        scores: msg.scores,
        perHoleData: msg.perHoleData,
      };
      this.snapshot.value = snapshot;
      this.hydrated.value = true;
      setCache(this.code, snapshot);
      // The organiser's seat lives on the tournament, so this is where a device learns
      // it: on a fresh phone after a recovery link, and again the moment somebody else
      // claims that name out from under it.
      syncOrganizerSeat(this.code, snapshot.tournament);
    } else if (msg.type === "ack") {
      const remaining = getOutbox(this.code).filter((o) => o.clientOpId !== msg.clientOpId);
      setOutbox(this.code, remaining);
      this.outboxSize.value = remaining.length;
    } else if (msg.type === "error") {
      // eslint-disable-next-line no-console
      console.warn("op rejected:", msg.message);
      if (msg.clientOpId) {
        // Stop retrying a permanently-rejected op — without this it would resend
        // forever on every reconnect while silently never syncing.
        const remaining = getOutbox(this.code).filter((o) => o.clientOpId !== msg.clientOpId);
        setOutbox(this.code, remaining);
        this.outboxSize.value = remaining.length;
      }
      this.lastError.value = msg.message;
      clearTimeout(this.lastErrorTimer);
      this.lastErrorTimer = setTimeout(() => (this.lastError.value = null), 6000);
    }
  }

  submit(op: ClientOp) {
    // The server refuses every op from a spectator, so queueing one here would put a
    // permanent resident in the outbox: the offline queue exists to survive a dropped
    // connection, and this would never drain however good the signal got. Screens
    // hide their controls from spectators too — this is the backstop for the one that
    // forgets.
    if (this.role === "spectator") return;

    const queued: QueuedOp = { clientOpId: crypto.randomUUID(), op };
    const outbox = [...getOutbox(this.code), queued];
    setOutbox(this.code, outbox);
    this.outboxSize.value = outbox.length;
    this.applyOptimistic(op);
    this.send(queued);
  }

  private flushOutbox() {
    for (const queued of getOutbox(this.code)) this.send(queued);
  }

  private send(queued: QueuedOp) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: "op", clientOpId: queued.clientOpId, op: queued.op }));
    }
  }

  /** Mirrors just enough of TournamentDO's applyOp so the UI updates instantly,
   * without waiting for the round trip. The server snapshot that follows is always
   * authoritative and overwrites this. */
  private applyOptimistic(op: ClientOp) {
    const snap = this.snapshot.value;
    if (!snap) return;
    const next: TournamentSnapshot = {
      tournament: snap.tournament,
      scores: [...snap.scores],
      perHoleData: [...snap.perHoleData],
    };

    if (op.type === "setScore") {
      const idx = next.scores.findIndex(
        (s) => s.roundId === op.roundId && s.playerId === op.playerId && s.hole === op.hole,
      );
      const entry = {
        roundId: op.roundId,
        playerId: op.playerId,
        hole: op.hole,
        strokes: op.strokes,
        enteredBy: this.role === "admin" ? "admin" : "",
        clientOpId: "",
        updatedAt: Date.now(),
      };
      if (idx >= 0) next.scores[idx] = entry;
      else next.scores.push(entry);
    } else if (op.type === "setPerHole") {
      const idx = next.perHoleData.findIndex(
        (d) => d.roundId === op.roundId && d.hole === op.hole && d.key === op.key,
      );
      const entry = {
        roundId: op.roundId,
        hole: op.hole,
        key: op.key,
        data: op.data,
        clientOpId: "",
        updatedAt: Date.now(),
      };
      if (idx >= 0) next.perHoleData[idx] = entry;
      else next.perHoleData.push(entry);
    } else if (op.type === "adminSetName") {
      next.tournament = { ...next.tournament, name: op.name };
    } else if (op.type === "adminSetStatus") {
      next.tournament = { ...next.tournament, status: op.status };
    } else if (op.type === "adminSetFinalTiebreaker") {
      next.tournament = { ...next.tournament, finalTiebreaker: op.rule };
    } else if (op.type === "adminSetPlayers") {
      const priorById = new Map(next.tournament.players.map((p) => [p.id, p]));
      next.tournament = {
        ...next.tournament,
        players: op.players.map((p) => ({
          ...p,
          token: null,
          claimedAt: priorById.get(p.id)?.claimedAt ?? null,
        })),
      };
    } else if (op.type === "adminSetRounds") {
      next.tournament = { ...next.tournament, rounds: op.rounds };
    } else if (op.type === "adminSetRoundLocked") {
      // Sent from the scorecard's own "Close this round" button, where the banner and
      // the steppers both key off `locked` — without this the screen sat unchanged
      // until the broadcast came back, which reads as a button that didn't work.
      next.tournament = {
        ...next.tournament,
        rounds: next.tournament.rounds.map((r) =>
          r.id === op.roundId ? { ...r, locked: op.locked } : r,
        ),
      };
    } else if (op.type === "donMode") {
      // The easter egg's one beat where waiting for the round trip would be felt: the
      // hold ends and the theft has to start on the spot. The server's own timestamp
      // arrives moments later and takes over, which is what keeps every device in the
      // group counting down the same minute — and if the server refuses (already used,
      // or not Don asking), that snapshot takes this straight back off the board.
      //
      // The op names nobody, so the subject is this device's own player, exactly as
      // the Durable Object will resolve it — from the token for a player, and from the
      // seat for an organiser who took Don's name off the roster. `playerId` holds
      // either, so this needs no case for it.
      const self = getAuth(this.code)?.playerId;
      if (!self) return;
      next.tournament = { ...next.tournament, donMode: { playerId: self, activatedAt: Date.now() } };
    }

    this.snapshot.value = next;
    setCache(this.code, next);
  }

  close() {
    this.closed = true;
    clearTimeout(this.reconnectTimer);
    clearTimeout(this.lastErrorTimer);
    this.ws?.close();
  }
}

const clients = new Map<string, TournamentClient>();

/**
 * One shared WS connection per tournament code, reused across screens.
 *
 * Keyed on the code *and* checked against the stored auth, because who this device is
 * in a game can change without the page reloading. Opening an organiser recovery link
 * on a phone that had already joined as a player is the ordinary way in: `setAuth`
 * writes the admin token, the setup screen's own `auth.role === "admin"` check passes,
 * and every HTTP admin call works — while the cached socket is still authenticated as
 * the player. Anything that goes over the socket instead ("Close this round", from the
 * scorecard) was then refused by the server as "admin only", on a screen that had just
 * proved the caller was the organiser.
 *
 * An organiser switching between organizing and playing is deliberately *not* one of
 * those changes: that moves neither token nor role, so the socket survives it. It has
 * to — the outbox is per tournament, not per identity, and an op queued offline under
 * one hat would be refused as "admin only" if it drained under the other, then dropped
 * for good. One credential on the wire in both modes is what makes the switch free.
 */
export function getClient(code: string): TournamentClient | null {
  const auth = getAuth(code);
  const existing = clients.get(code);

  if (existing && auth && existing.token === auth.token && existing.role === auth.role) {
    return existing;
  }
  if (existing) {
    existing.close();
    clients.delete(code);
  }
  if (!auth) return null;

  const client = new TournamentClient(code, auth.token, auth.role);
  clients.set(code, client);
  return client;
}
